import { useCallback, useContext, useState } from "react";
import { EntityConstants, IGenericRequestParameters, InferSortByOptions } from "../../../api/GenericTypes";
import { GenericEntity } from "../../../Entities/GenericComponents/GenericComponents";
import { SessionContext } from "../../contexts/SessionContext";
import { showtoast } from "../../overlays/Toasts/showtoast";

export const PdfOutputTypes = ["Combined", "Separate"] as const;
export type PdfOutputType = (typeof PdfOutputTypes)[number];
export const pdfOutputTypeToLabel = (type: PdfOutputType) => {
  switch (type) {
    case "Combined":
      return "Combined";
    case "Separate":
      return "Separate";
    default:
      return "-";
  }
};
export const PdfOutputTypeFormOptions = PdfOutputTypes.map((c) => ({
  id: c,
  label: pdfOutputTypeToLabel(c),
  value: c,
}));

export const PdfPageSeparators = ["None", "Gap", "PageBreak", "HorizontalLine"] as const;
export type PdfPageSeparator = (typeof PdfPageSeparators)[number];
export const pdfPageSeparatorToLabel = (type: PdfPageSeparator) => {
  switch (type) {
    case "None":
      return "None";
    case "Gap":
      return "Gap";
    case "PageBreak":
      return "Separate pages";
    case "HorizontalLine":
      return "Horizontal Line";
    default:
      return "-";
  }
};
export const PdfPageSeparatorFormOptions = PdfPageSeparators.map((c) => ({
  id: c,
  label: pdfPageSeparatorToLabel(c),
  value: c,
}));

export interface PdfSettings {
  fileName?: string;
  outputType?: PdfOutputType;
  separator?: PdfPageSeparator;
}
interface DownloadPdfSettings<
  Entity extends GenericEntity,
  Filters extends { [property: string]: any } & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> {
  filters: { [property: string]: any } & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>;
  pdfSettings: PdfSettings;
  publicBaseUrl: string;
}

export const useExportPDF = <
  Entity extends GenericEntity,
  Filters extends { [property: string]: any } & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityConstants,
  filters,
  pdfSettings,
}: {
  entityConstants: EntityConstants;
  filters?: Filters;
  pdfSettings?: PdfSettings;
}) => {
  const { api } = useContext(SessionContext);
  const [loading, setLoading] = useState<boolean>(false);

  const exportToPdf = useCallback(async () => {
    setLoading(true);
    api
      .post(`${entityConstants.resource}/export/pdf`, {
        filters,
        pdfSettings,
        publicBaseUrl: window.location.origin,
      } as DownloadPdfSettings<Entity, Filters>)
      .then((result) => {
        showtoast(
          "success",
          <div className="flex gap-5" style={{ flexDirection: "column" }}>
            <div style={{ fontWeight: 600 }}>The export job has been created successfully.</div>
            <div>Files will be available for download shortly.</div>
          </div>,
          undefined,
          5000
        );

        setLoading(false);
      })
      .catch((e) => {
        showtoast("error", `Error: ${e.description ?? e.message ?? "Unknown error"}`);
        setLoading(false);
      });
  }, [api, entityConstants.resource, filters, pdfSettings]);

  return { exportToPdf, loading };
};
